import { Box, ButtonV2, Dropdown, ICONS_NAME, IconV2, TableV2, Typography } from '@/elements';
import React from 'react';
import { COLORS, OrderTypes } from '@/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { saveNavigateBackFrom } from '@/utils';
import { DropdownSearch } from '../../../components/DropdownSearch/DropdownSearch';
import dateFormat from 'dateformat';
import { statusDefinitionOptions } from '@/common/pages/accounts/Account/store/index.js';
import { Color } from '@/utils';

export const AccountTable = ({
  totalPages,
  data,
  loading,
  totalCount,
  onPageChange,
  onSortingChange,
  onFiltersChange,
  queryParams,
  setIsDeleted,
  loginByEmployeeHandler,
}) => {
  const sortingInitial = {
    id: queryParams.sortKey || '',
    desc: queryParams.sortDirection === OrderTypes.Desc,
  };
  const statusDefinition = (status, deletedAt) => {
    if (deletedAt) {
      return dateFormat(deletedAt, 'dd/mm/yy');
    }

    return statusDefinitionOptions[status];
  };
  const navigate = useNavigate();
  const location = useLocation();

  const BadgeComponent = () => (
    <Box width="25px" height="25px" justify="center" align="center" background={Color.primary}>
      <Typography variant="default" color={COLORS.backgroundLight}>
        A
      </Typography>
    </Box>
  );

  const columns = [
    {
      id: 'id',
      header: 'ID',
      body: 'id',
      enableColumnFilter: true,
      enableSorting: true,
      minWidth: 155,
      width: 155,
    },
    {
      id: 'name',
      header: 'Account name',
      body: 'name',
      enableColumnFilter: true,
      enableSorting: false,
      width: 400,
    },
    {
      id: 'createdAt',
      header: 'Created At',
      body: ({ getValue }) => dateFormat(getValue(), 'dd/mm/yy'),
      enableColumnFilter: false,
      enableSorting: false,
      width: 400,
    },
    {
      id: 'status',
      header: 'Status',
      body: ({ row }) => statusDefinition(row?.original?.accountStatus, row?.original?.deletedAt),
      enableColumnFilter: false,
      enableSorting: false,
      width: 400,
    },
    {
      id: 'productStatus',
      header: 'Product status',
      body: ({ row }) => {
        return row?.original?.deletedAt ? (
          row?.original?.deletedAt
        ) : (
          <Box direction="column" gap="5px">
            <Typography variant="body2">
              AP: {row?.original?.productStatus?.applicationProtection || ''}
            </Typography>
            <Typography variant="body2">
              DNS: {row?.original?.productStatus?.dnsHosting || ''}
            </Typography>
            <Typography variant="body2">
              NP: {row?.original?.productStatus?.networkProtectionV2 || ''}
            </Typography>
          </Box>
        );
      },
      width: 400,
    },
    {
      id: 'action_old_lk',
      header: 'Login as',
      body: ({ row }) => {
        const { partnerClientAccountEmployees = [] } = row?.original || {};

        return (
          <DropdownSearch
            row={row}
            isSearchable
            defaultOptions={[
              ...(partnerClientAccountEmployees
                ?.filter(({ deletedAt, status }) => !deletedAt && status === 'ACTIVE')
                ?.map(({ email, id, isInDefaultGroup }) => ({
                  value: email,
                  label: (
                    <Box gap="8px">
                      <Typography>{email}</Typography>

                      {isInDefaultGroup ? <Typography>{BadgeComponent()}</Typography> : null}
                    </Box>
                  ),
                  id,
                }))
                ?.sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase())) || []),
            ]}
            onClick={({ value }) => {
              loginByEmployeeHandler(value);
            }}
          >
            <ButtonV2
              size="small"
              variant="ghost"
              disabled={!partnerClientAccountEmployees?.length}
            >
              <IconV2 size={18} color={COLORS.grayDark} name={ICONS_NAME.arrowRight} />
            </ButtonV2>
          </DropdownSearch>
        );
      },
    },
    {
      id: 'action',
      meta: {
        justify: 'center',
      },
      width: 50,
      minWidth: 50,

      header: '',
      body: ({ row }) => {
        const { name, id, deletedAt } = row?.original || {};

        return (
          <Box>
            <Dropdown
              onClick={({ id, value }) => {
                if (value === 'delete' && id) {
                  setIsDeleted({
                    deleted: true,
                    id,
                    name,
                  });
                } else {
                  saveNavigateBackFrom(location);
                  navigate(`/edit-account/0/${id}`, { state: { from: location } });
                }
              }}
              options={[
                {
                  value: 'edit',
                  label: 'Edit',
                  id,
                  disabled: Boolean(deletedAt),
                },

                {
                  value: 'delete',
                  label: 'Delete',
                  id,
                  color: COLORS.errorLight,
                  disabled: Boolean(deletedAt),
                },
              ]}
            >
              <ButtonV2 size="small" variant="ghost">
                <IconV2 size={18} color={COLORS.grayDark} name={ICONS_NAME.dot} />
              </ButtonV2>
            </Dropdown>
          </Box>
        );
      },
    },
  ];

  return (
    <TableV2
      data={data || []}
      loading={loading}
      columns={columns}
      page={queryParams.page}
      size={queryParams.limit}
      totalPages={totalPages}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onSortingChange={onSortingChange}
      onFiltersChange={onFiltersChange}
      initialStateSort={sortingInitial}
    />
  );
};
